import React, { useState, useEffect } from "react";
import "react-responsive-modal/styles.css";
import DatePicker from "react-date-picker";
import { Modal } from "react-responsive-modal";
import { globalAlert, globalLoader } from "../../actions/commonActions";
import { UPLOAD_IMAGE } from "../../actionTypes/types";
import { getCsvFile } from "../../superadmin/_actions";
import FileDownload from "js-file-download";
import { logOut } from "../../_shared/commonFunction";
import InputComponent from "../_common/inputComponent";
import LmsOrgFields from "./lmsOrgFields";
import { useTranslation } from "react-i18next";
import { useRef } from "react";

const AddOrgModal = (props) => {
  const { t } = useTranslation();
  const [orgName, setOrgName] = useState("");
  const [postCode, setPostcode] = useState(null);
  const [logo, setLogo] = useState("");
  const [logoImg, setLogoImg] = useState("");
  const [orgNameErr, setOrgNameErr] = useState(false);
  const [postCodeErr, setPostcodeErr] = useState(false);
  const [logoErr, setLogoErr] = useState(false);
  const [logoFile, setLogoFile] = useState("");
  const [copyrightText, setCopyrightText] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [physicalCardEnabled, setPhysicalCardEnabled] = useState(false);

  // Learning-feature fields (managed by the shared LmsOrgFields component).
  const [lmsState, setLmsState] = useState({
    lmsEnabled: false,
    onlyLmsEnabled: false,
    lmsShortName: "",
    shortNameValid: false,
  });
  const [lmsErr, setLmsErr] = useState("");
  const prevShort = useRef("");

  // Surface a short-name 204 from the server (passed down by the parent).
  useEffect(() => {
    setLmsErr(props?.shortNameServerError || "");
  }, [props?.shortNameServerError]);

  const onLmsChange = (s) => {
    if (s.lmsShortName !== prevShort.current) {
      setLmsErr("");
      prevShort.current = s.lmsShortName;
    }
    setLmsState(s);
  };

  useEffect(() => {
    setPropsToState(); // eslint-disable-next-line
  }, [props?.data]);

  const setPropsToState = () => {
    setOrgName(props?.data?.orgName);
    setLogoImg(props?.data?.brandLogo);
    setCopyrightText(props?.data?.copyrightText);
  };
  const handleValidation = () => {
    let flag = true;
    if (!orgName) {
      setOrgNameErr(true);
      flag = false;
    }
    if (!postCode && !props?.edit) {
      setPostcodeErr(true);
      flag = false;
    }
    if (!logo && !props?.edit) {
      setLogoErr(true);
      flag = false;
    }
    // Short Name is required + valid only when the Learning feature is enabled.
    if (lmsState.lmsEnabled) {
      if (!lmsState.lmsShortName) {
        setLmsErr(
          t(
            "superadmin.lms.shortNameRequired",
            "Short Name is mandatory when the Learning feature is enabled."
          )
        );
        flag = false;
      } else if (!lmsState.shortNameValid) {
        setLmsErr(
          t(
            "superadmin.lms.shortNameInvalid",
            "Use 3–30 lowercase letters, numbers or hyphens; no spaces and no leading/trailing hyphen."
          )
        );
        flag = false;
      }
    }

    return flag;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (await handleValidation()) {
      let formData = new FormData();
      formData.append("copyrightText", copyrightText);
      formData.append("orgName", orgName);
      formData.append("file", logo);
      formData.append("areaCodesFile", postCode);
      formData.append('isAdminMfaEnabled', isActive)
      formData.append('isPhysicalCardEnabled', physicalCardEnabled)
      // Learning feature — always send the flag; short name only when ON.
      // (Approved Email Domains AND the Default Region are managed by the org
      // admin in LMS → Settings, not here.)
      formData.append("lmsEnabled", String(lmsState.lmsEnabled));
      formData.append("onlyLmsEnabled", String(lmsState.onlyLmsEnabled));
      if (lmsState.lmsEnabled) {
        formData.append("lmsShortName", lmsState.lmsShortName);
      }
      await props.closeModal(formData);
    }
  };

  const setLogoFunc = (ev) => {
    if (
      ev?.target?.files[0]?.type === "image/jpeg" ||
      ev?.target?.files[0]?.type === "image/png"
    ) {
      const file = ev.target.files[0];
      setLogo(file);
      const url = URL.createObjectURL(file);
      setLogoFile(url);
    } else {
      globalAlert("error", t("superadmin.addOrg.onlyPngJpegAllowed"));
    }
  };

  const setPostalFunc = (ev) => {
    if (
      ev?.target?.files[0]?.type === "application/vnd.ms-excel" ||
      ev?.target?.files[0]?.type === "text/csv"
    ) {
      const file = ev.target.files[0];
      setPostcode(file);
    } else {
      globalAlert("error", t("superadmin.addOrg.onlyCsvAllowed"));
    }
  };

  const downloadFileFormat = async (e) => {
    e.preventDefault();
    try {
      globalLoader(true);
      let res = await getCsvFile();

      if (res) {
        FileDownload(res, "postalcode.csv");
      } else {
        globalAlert("error", res?.data?.message);
      }
      globalLoader(false);
    } catch (error) {
      globalAlert("error", "Unauthorised");
      logOut(props?.history, "/superadmin/login");
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={() => props.closeModal()}
      center
      classNames={{ modal: "admin-modal modal-own" }}
    >
      <div>
        <p className="admin-banner">
          {t(props?.edit ? "superadmin.addOrg.editTitle" : "superadmin.addOrg.addTitle")}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="form-own px-3">
        <div className="row px-2">
          <div className="col-12 px-2 pb-3">
            <div className="form-own-group">
              <label className="form-own-label d-block mb-2">
                {t("superadmin.addOrg.uploadSectorCodeLabel")} <span className="color-red">*</span>
              </label>
              <div className="d-flex justify-content-end pb-2">
                <button
                  className="btn secondary-btn btn-sm"
                  onClick={downloadFileFormat}
                >
                  <i className="fas fa-arrow-down mr-1"></i> {t("superadmin.addOrg.downloadFormat")}
                </button>
              </div>
              <div className="upload-btn upload-btn-dashed" id="previewLogo">
                <div className="show-icon-wrapper pb-2 mb-1 font-14">
                  <i className="fas fa-file-csv font-16" aria-hidden="true"></i>
                  <span className="pl-1">{postCode?.name}</span>
                </div>

                <div className="upload-btn-txt btn">{t("superadmin.addOrg.addFile")}</div>
                <input
                  type="file"
                  accept=".csv"
                  className="upload-input cursor"
                  onChange={setPostalFunc}
                />
              </div>
              <div className="error-text">
                {!postCode && postCodeErr && (
                  <span>{t("superadmin.addOrg.areacodeFileRequired")}</span>
                )}
              </div>
            </div>
          </div>
          {props?.data?.areaCode?.length > 0 && props?.edit && (
            <div className="col-12 px-2">
              <div className="form-own-group">
                <label htmlFor="postCode" className="form-own-label">
                  {t("superadmin.addOrg.currentAreacodeList")}
                </label>
                <ul className="list-unstyled arecode-list">
                  {props.data.areaCode.map((area, index) => (
                    <li key={index}>{area}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="col-6 px-2">
            <div className="form-own-group">
              <InputComponent
                placeholder={t("superadmin.addOrg.namePlaceholder")}
                label={
                  <>
                    {t("superadmin.addOrg.nameLabel")} <span className="color-red">*</span>
                  </>
                }
                type="text"
                name="name"
                theme="white"
                value={orgName}
                handleCallback={(val) => {
                  setOrgName(val);
                }}
              />
              <div className="error-text">
                {!orgName && orgNameErr && (
                  <span>{t("superadmin.addOrg.orgNameRequired")}</span>
                )}
              </div>
            </div>
          </div>
          <div className="col-6 px-2">
            <div className="form-own-group">
              <InputComponent
                placeholder={t("superadmin.addOrg.copyrightTextPlaceholder")}
                label={t("superadmin.addOrg.copyrightTextLabel")}
                type="text"
                name="postCode"
                theme="white"
                maxLength="50"
                value={copyrightText}
                handleCallback={(val) => {
                  setCopyrightText(val);
                }}
              />
            </div>
          </div>
          <div className="form-own-group toggle-row px-2">
              <label htmlFor="switch" className="form-own-label mb-0">
                {t("superadmin.addOrg.isAdminMfaRequired")}
              </label>
              <input
                className="toggle-input"
                type="checkbox"
                id="switch"
                onChange={(ev) => {
                  setIsActive(ev.target.checked);
                }}
                checked={isActive}
              />
              <label className="toggle-label" htmlFor="switch">
                {t("superadmin.addOrg.toggleLabel")}
              </label>
            </div>
            <div className="form-own-group toggle-row px-2">
              <label htmlFor="switchPhysicalCardAdd" className="form-own-label mb-0">
                {t("superadmin.addOrg.isPhysicalCardEnabled")}
              </label>
              <input
                className="toggle-input"
                type="checkbox"
                id="switchPhysicalCardAdd"
                onChange={(ev) => {
                  setPhysicalCardEnabled(ev.target.checked);
                }}
                checked={physicalCardEnabled}
              />
              <label className="toggle-label" htmlFor="switchPhysicalCardAdd">
                {t("superadmin.addOrg.toggleLabel")}
              </label>
            </div>

            <LmsOrgFields
              initial={null}
              serverShortNameError={lmsErr}
              theme="white"
              onChange={onLmsChange}
            />

          <div className="col-12 px-2">
            <div className="form-own-group">
              <label className="form-own-label">
                {t("superadmin.addOrg.uploadLogoLabel")} <span className="color-red">*</span>
              </label>
              <div className="upload-btn upload-btn-dashed" id="previewLogo">
                <div className="show-icon-wrapper pb-2 mb-1">
                  <img
                    alt="preview logo"
                    id="previewLogo"
                    src={logoFile ? logoFile : logoImg ? logoImg : UPLOAD_IMAGE}
                  />
                </div>

                <div className="upload-btn-txt btn">
                  + {logoImg || logoFile ? t("superadmin.addOrg.changeFile") : t("superadmin.addOrg.addFileText")} {t("superadmin.addOrg.fileLabel")}
                </div>
                <input
                  type="file"
                  accept="image/x-png,image/jpeg"
                  className="upload-input cursor"
                  onChange={setLogoFunc}
                />
              </div>
              <div className="error-text">
                {!logo && logoErr && <span>{t("superadmin.addOrg.logoRequired")}</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="admin-btn">
          <button
            onClick={() => props.closeModal()}
            className="btn secondary-btn mr-2"
            type="button"
          >
            {t("common.back")}
          </button>
          <button type="submit" className="btn primary-btn ml-2">
            {t("common.submit")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddOrgModal;
